import React, { useState, useMemo, useEffect } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { Avatar } from "../shared/Avatar";
import { PhotoUploader } from "../shared/PhotoUploader";
import { apiCreateExpense, apiUpdateExpense, apiListPayees } from "./api";
import { todayLocalISO } from "../../lib/localDate";

const EXPENSE_CATEGORIES = [
  "Mortgage/Rent", "Staff Wages", "Guest Speakers", "Equipment/Materials",
  "Pastor Stipend", "Utilities", "Legal Fees", "Bank Fees", "Benevolence",
  "Missionary Support", "Staff Travel", "Ministry Education", "Licenses",
  "Insurances", "Payroll Taxes", "Other",
];
const PAYEE_TYPES = ["Vendor", "Individual"];

function todayISO() { return todayLocalISO(); }
function dollarsToCents(v) {
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export default function ExpenseEntryModal({ open, members, editRecord, onClose, onSaved, currencySymbol = "$", thresholds }) {
  const isEdit = Boolean(editRecord);
  const [payeeName, setPayeeName] = useState(editRecord?.payeeName || "");
  const [payeeType, setPayeeType] = useState(editRecord?.payeeType || "Vendor");
  const [isChurchMember, setIsChurchMember] = useState(Boolean(editRecord?.linkedMemberId));
  const [linkedMemberId, setLinkedMemberId] = useState(editRecord?.linkedMemberId || "");
  const [memberSearch, setMemberSearch] = useState("");
  const [date, setDate] = useState(editRecord?.date || todayISO());
  const [amountDollars, setAmountDollars] = useState(
    editRecord ? (editRecord.amountCents / 100).toFixed(2) : ""
  );
  const [category, setCategory] = useState(editRecord?.category || "Mortgage/Rent");
  const [categoryOther, setCategoryOther] = useState(editRecord?.categoryOther || "");
  const [receiptImage, setReceiptImage] = useState(editRecord?.receiptImage || "");
  const [notes, setNotes] = useState(editRecord?.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [showPayeeDropdown, setShowPayeeDropdown] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiListPayees().then(setPayeeOptions).catch(() => setPayeeOptions([]));
  }, [open]);

  const filteredPayees = useMemo(() => {
    const q = payeeName.trim().toLowerCase();
    if (!q) return payeeOptions.slice(0, 6);
    return payeeOptions.filter(p => p.payeeName.toLowerCase().includes(q)).slice(0, 6);
  }, [payeeName, payeeOptions]);

  const filteredMembers = useMemo(() => {
    if (!isChurchMember) return [];
    const q = memberSearch.trim().toLowerCase();
    if (!q) return (members || []).slice(0, 8);
    return (members || [])
      .filter(m => `${m.firstName || ""} ${m.lastName || ""}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [members, memberSearch, isChurchMember]);

  const selectedMember = useMemo(
    () => (members || []).find(m => m.id === linkedMemberId),
    [members, linkedMemberId]
  );

  const amountCents = dollarsToCents(amountDollars);
  const approvalNotice = useMemo(() => {
    if (!thresholds || amountCents <= 0) return null;
    if (amountCents >= thresholds.admin) {
      return { level: "admin", text: "Requires Admin approval." };
    }
    return { level: "leader", text: "Requires Department Leader approval." };
  }, [amountCents, thresholds]);

  if (!open) return null;

  const submit = async () => {
    setError("");
    if (!payeeName.trim()) { setError("Payee name is required."); return; }
    if (amountCents <= 0) { setError("Amount must be greater than 0."); return; }
    if (category === "Other" && !categoryOther.trim()) { setError("Please specify the category."); return; }
    if (!receiptImage) { setError("Receipt upload is required."); return; }
    setSaving(true);
    try {
      const payload = {
        payeeName: payeeName.trim(),
        payeeType,
        linkedMemberId: isChurchMember ? linkedMemberId || null : null,
        date,
        amountCents,
        category,
        categoryOther: category === "Other" ? categoryOther.trim() : null,
        receiptImage,
        notes: notes.trim() || null,
      };
      if (isEdit) {
        const updated = await apiUpdateExpense(editRecord.recordId, payload);
        onSaved(updated);
      } else {
        const created = await apiCreateExpense(payload);
        onSaved(created);
      }
      onClose();
    } catch (e) {
      setError(e.message || "Could not save expense.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="expense-entry-modal">
      <div className="modal" style={{ maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <CustomIcon name="expense" size={22} />
            <h2 style={{ margin: 0 }}>{isEdit ? "Edit Expense" : "Log Expense"}</h2>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="expense-modal-close">×</button>
        </div>
        <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ position: "relative" }}>
            <label className="form-label">Payee Name</label>
            <input
              type="text"
              className="form-input"
              value={payeeName}
              onChange={(e) => { setPayeeName(e.target.value); setShowPayeeDropdown(true); }}
              onFocus={() => setShowPayeeDropdown(true)}
              onBlur={() => setTimeout(() => setShowPayeeDropdown(false), 150)}
              placeholder="Vendor or person name"
              data-testid="expense-payee-name"
            />
            {showPayeeDropdown && filteredPayees.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 8, zIndex: 10, maxHeight: 180, overflowY: "auto" }}>
                {filteredPayees.map(p => (
                  <div
                    key={p.payeeName}
                    onClick={() => { setPayeeName(p.payeeName); if (p.payeeType) setPayeeType(p.payeeType); if (p.linkedMemberId) { setIsChurchMember(true); setLinkedMemberId(p.linkedMemberId); } setShowPayeeDropdown(false); }}
                    style={{ padding: 8, cursor: "pointer", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}
                    data-testid={`expense-payee-suggestion-${p.payeeName}`}
                  >
                    <div style={{ fontWeight: 500 }}>{p.payeeName}</div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>{p.payeeType || "—"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isChurchMember}
              onChange={(e) => {
                setIsChurchMember(e.target.checked);
                if (!e.target.checked) setLinkedMemberId("");
                else setPayeeType("Individual");
              }}
              data-testid="expense-ischurchmember"
            />
            <span>Is this payee a church member?</span>
          </label>

          {isChurchMember && (
            <div>
              <label className="form-label">Link to Member</label>
              {selectedMember ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", borderRadius: 8 }}>
                  <Avatar photo={selectedMember.photo} name={`${selectedMember.firstName} ${selectedMember.lastName}`} memberId={selectedMember.id} status={selectedMember.memberStatus} size={32} />
                  <div style={{ flex: 1 }}>{selectedMember.firstName} {selectedMember.lastName}</div>
                  <button className="btn btn-outline btn-sm" onClick={() => setLinkedMemberId("")}>Change</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by name…"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    data-testid="expense-linkedmember-search"
                  />
                  {filteredMembers.length > 0 && (
                    <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)", borderRadius: 8, marginTop: 4 }}>
                      {filteredMembers.map(m => (
                        <div
                          key={m.id}
                          onClick={() => { setLinkedMemberId(m.id); setMemberSearch(""); setPayeeType("Individual"); if (!payeeName) setPayeeName(`${m.firstName} ${m.lastName}`); }}
                          style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, cursor: "pointer", borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)" }}
                          data-testid={`expense-linkedmember-result-${m.id}`}
                        >
                          <Avatar photo={m.photo} name={`${m.firstName} ${m.lastName}`} memberId={m.id} status={m.memberStatus} size={28} />
                          <span>{m.firstName} {m.lastName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <label className="form-label">Payee Type</label>
            <select className="form-input" value={payeeType} onChange={(e) => setPayeeType(e.target.value)} data-testid="expense-payeetype">
              {PAYEE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="expense-date" />
          </div>

          <div>
            <label className="form-label">Amount ({currencySymbol})</label>
            <input
              type="text"
              inputMode="decimal"
              className="form-input"
              placeholder="0.00"
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              data-testid="expense-amount"
            />
            {approvalNotice && (
              <div
                style={{
                  marginTop: 6,
                  padding: "6px 10px",
                  borderRadius: 6,
                  background: approvalNotice.level === "admin" ? "rgba(200,50,50,0.15)" : "color-mix(in srgb, var(--kc-accent) 12%, transparent)",
                  color: approvalNotice.level === "admin" ? "#f2a2a2" : "var(--kc-accent)",
                  fontSize: 13,
                }}
                data-testid="expense-approval-notice"
              >
                {approvalNotice.text}
              </div>
            )}
          </div>

          <div>
            <label className="form-label">Category</label>
            <select className="form-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="expense-category">
              {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {category === "Other" && (
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Utilities, Repair, Guest speaker"
                value={categoryOther}
                onChange={(e) => setCategoryOther(e.target.value)}
                style={{ marginTop: 6 }}
                data-testid="expense-category-other"
              />
            )}
          </div>

          <div>
            <label className="form-label">Upload receipt photo — required</label>
            <PhotoUploader value={receiptImage} onChange={setReceiptImage} data-testid="expense-receipt-uploader" />
          </div>

          <div>
            <label className="form-label">Notes (optional)</label>
            <textarea className="form-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="expense-notes" />
          </div>

          {error && <div className="alert-error" data-testid="expense-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !receiptImage}
            data-testid="expense-save-btn"
          >
            {saving ? "Submitting…" : (isEdit ? "Save Changes" : "Submit for Approval")}
          </button>
        </div>
      </div>
    </div>
  );
}
